'use client';

/**
 * 브랜드 공유 페이지의 값 — 스냅숏 하나를 상세 머리·개요가 받는 모양으로 편다.
 *
 * 🔑 기간은 늘 최근 30일이다(링크에 기간 칸이 없다) — 제품군·채널·국가·페르소나는 공유한 사람의 값이다.
 * 🔑 화면 안의 어떤 것도 눌리지 않는다(껍데기가 `inert` 로 막는다) — 손짓은 전부 빈 함수다.
 * ⚠️ 즐겨찾기·추적은 이 사람의 것이 없어 「안 담음 · 추적 안 함」으로 선다. Research 띠의 플랜 수는 비운다(플랜이 없다).
 */

import type { ShareBrandPayload } from 'src/types/hub/hub-share';

import { useMemo, useState } from 'react';

import { brandQualityNotice } from 'src/utils/brand/brand-quality-doc';
import { personaTokensToSelection } from 'src/utils/brand/brand-persona-persist';

import { toHubResearchCopy } from 'src/components/cmui/hub/hub-research-copy';

import { toBrandHubPeriod } from '../utils/brand-hub-period';
import { SHARE_BRAND_DAYS } from '../utils/brand-hub-share-link';
import { brandHubProductRegion } from '../utils/brand-hub-product-region';
import { toBrandHubShareDisplay, toBrandHubShareOverview } from '../utils/brand-hub-share-display';

import type { BrandFace } from '../constants/brand-hub-faces';

const OVERVIEW: BrandFace = 'overview';
/** 플랜이 없는 사람 — Research 띠의 플랜 칸을 비운다 */
const NO_LIMITS = { searchDays: 0, searchRows: 0, researchMonthly: null };
const noop = () => undefined;

/**
 * @param data 서버가 지은 공유 스냅숏
 * @returns view 가 부품마다 펴서 넘기는 묶음(`head` · `actions` · `overview` · `research`)
 */
export function useBrandHubShareViewModel(data: ShareBrandPayload) {
  const { cond } = data;
  /** 기간은 늘 최근 30일이다 — 링크에 기간 칸이 없다 */
  const period = toBrandHubPeriod(SHARE_BRAND_DAYS, null);
  const [rawIngredients, setRawIngredients] = useState(false);
  const [rawConcerns, setRawConcerns] = useState(false);

  const view = useMemo(() => toBrandHubShareDisplay(data), [data]);
  const overviewData = useMemo(
    () => toBrandHubShareOverview(data, view, { ingredients: rawIngredients, concerns: rawConcerns }),
    [data, view, rawIngredients, rawConcerns],
  );

  const scopeOn = {
    category: Boolean(cond.main),
    channels: cond.channels.length > 0,
    countries: cond.countries.length > 0,
    persona: Boolean(personaTokensToSelection(cond.personas ?? [])),
    period: false,
  };
  const productRegion = brandHubProductRegion(cond.countries);

  return {
    head: {
      name: data.brand,
      imageUrl: view.faces.get(data.brand.toLowerCase()) ?? '',
      periodLabel: period.label,
      head: view.head,
      scopeOn,
      productRegion,
      face: OVERVIEW,
      onChangeFace: noop,
      qualityNotice: brandQualityNotice(data.notice, data.uniqueMentions != null),
      rankLabel: view.rankLabel,
    },
    actions: { onShare: noop, saved: false, saving: false, onToggleSave: noop, tracked: false, trackedLabel: '', trackedHref: '', onTrack: noop },
    overview: {
      name: data.brand,
      periodLabel: period.label,
      data: overviewData,
      adCards: view.adCards,
      productCards: view.productCards,
      accountProfiles: view.profiles,
      rawIngredients,
      onChangeRawIngredients: setRawIngredients,
      rawConcerns,
      onChangeRawConcerns: setRawConcerns,
      scopeOn,
      productRegion,
      onOpenBrand: noop,
      onOpenCreator: noop,
      onOpenFace: noop,
      creatorsHref: '',
      publicGrid: { profiles: view.profiles },
    },
    research: toHubResearchCopy('social', { subject: 'brand', name: data.brand, limits: NO_LIMITS, domesticMalls: 0 }),
  };
}
